/**
 * チャレンジ定義 — 日本で最も難しい問題 (設計仕様書 v1.0 の §19〜§25)
 *
 * コースが「武器を学ぶ」場で、ロードマップが「現在地を知る」場なら、
 * ここは「限界を試す」場になる。
 *
 * **いまは問題文だけを出している。** 採点も提出もまだ無い。先にカードを
 * 出すのは、どの問題に人が集まるかを見てから1本だけ作るため。作ってから
 * 需要を測ると、外した1本に数か月かかる。
 *
 * 難易度は1段しか持たない。易しい問題はコースと道場が受け持っていて、
 * ここに中くらいの段を足すと「最も難しい」が薄まる。
 */

/** 一覧の絞り込みに出す分野。1つの問題が複数に属してよい */
export type ChallengeCategory =
  | "engineering"
  | "ai"
  | "security"
  | "data"
  | "growth"
  | "product";

/**
 * 公開の段。
 *
 * designing  問題文だけある。挑戦の口はまだ無い
 * open       解いて提出できる
 */
export type ChallengeStatus = "designing" | "open";

/**
 * 問題の形。
 *
 * scenario  状況があって、断片的な情報を自分でつなぐ
 * mystery   提示された結末への筋道を組み立てる
 */
export type ChallengeFormat = "scenario" | "mystery";

/**
 * 調査卓に載る実行環境の種類。
 *
 * 問題文は同じ面に並ぶが、採点の道具は種類ごとに違う。
 * 種類で引けるようにしておくと、sql の卓を作ったときに
 * 他の種類へ回収が波及しない
 */
export type ChallengeRuntimeKind =
  | "sql"
  | "code"
  | "web"
  | "security"
  | "linux"
  | "ai-agent"
  | "data-sim";

/**
 * 解くのに要る力。
 *
 * roadmapSlug と nodeId が揃っているものは、ロードマップの該当ノードへ
 * 飛ばす。揃っていない力は、その分野の細粒度マップをまだ作っていない。
 * リンクの無い札として出す (行き先の無いリンクにしない)
 */
export interface ChallengeRequiredSkill {
  label: string;
  roadmapSlug?: string;
  nodeId?: string;
}

export interface Challenge {
  slug: string;
  /** 通し番号の見せ方。HELL #001 */
  code: string;
  /** 命令形の1文。カードの主役はこれ */
  title: string;
  /** 分野の言い方。題の下に小さく出す */
  field: string;
  categories: ChallengeCategory[];
  /** 状況・制約・依頼。改行はそのまま行送りにする */
  brief: string;
  /** 何が確認されたら通ったことにするか。点数は出さない */
  evidence: string[];
  requiredSkills: ChallengeRequiredSkill[];
  aiAllowed: boolean;
  internetAllowed: boolean;
  status: ChallengeStatus;

  /**
   * 以下は調査卓つきの問題だけが持つ。既存の 6 問は書かなくても
   * そのまま動く。runtime の manifestId が指す段の並びは
   * challenge-manifests.ts にある
   */
  format?: ChallengeFormat;
  runtime?: { kind: ChallengeRuntimeKind; manifestId: string };
  /** 問題文の改版番号。attempt はこの番号に紐付けて記録する */
  version?: number;
  estimatedMinutes?: { min: number; max: number };
  tags?: string[];
}

export const CHALLENGE_CATEGORY_LABELS: Record<ChallengeCategory, string> = {
  engineering: "エンジニアリング",
  ai: "AI",
  security: "セキュリティ",
  data: "データ",
  growth: "グロース",
  product: "プロダクト",
};

export const CHALLENGES: Challenge[] = [
  {
    slug: "stop-double-charge",
    code: "HELL #001",
    title: "二重決済を止めろ。",
    field: "バックエンド / データベース",
    categories: ["engineering", "data"],
    brief:
      "ECサービスで、ごく稀に二重請求が発生しています。\n発生条件は分かっていません。\n再現手順もありません。分かっているのは、月に数件の問い合わせが来ることだけです。\n\n既存の注文データを壊さずに、原因を特定して直してください。",
    evidence: [
      "二重請求が起きる筋道を、コードと実行順で説明できている",
      "その筋道を踏む自動テストが、修正前に落ちて修正後に通る",
      "既存の注文データが1件も壊れていない",
    ],
    requiredSkills: [
      { label: "トランザクション" },
      { label: "同時実行" },
      { label: "HTTP" },
      { label: "Webhook" },
      { label: "テスト" },
    ],
    aiAllowed: true,
    internetAllowed: true,
    status: "designing",
  },
  {
    slug: "automate-ai-support",
    code: "HELL #002",
    title: "AI問い合わせ対応を70%自動化せよ。",
    field: "AI / 業務自動化",
    categories: ["ai", "product"],
    brief:
      "1日200件の問い合わせに、3人で答えています。\n同じ質問が何度も来ますが、少しずつ違います。\n\n返答の7割を自動で返しつつ、誤答を人手の対応より増やさないでください。\n「分からない」と言わせる線引きも、あなたが決めます。",
    evidence: [
      "自動で返した割合と、誤答の割合を同じ基準で数えている",
      "AIに答えさせない条件が明文化され、実装で強制されている",
      "過去の問い合わせログで再現して測れる",
    ],
    requiredSkills: [
      { label: "プロンプト設計" },
      { label: "RAG" },
      { label: "評価データの作り方" },
      { label: "API連携" },
    ],
    aiAllowed: true,
    internetAllowed: true,
    status: "designing",
  },
  {
    slug: "close-the-tenant-leak",
    code: "HELL #003",
    title: "他社データが見える穴を塞げ。",
    field: "セキュリティ",
    categories: ["security", "engineering"],
    brief:
      "複数の会社が同じ画面を使うサービスです。\nある条件で、他社のデータが見えます。\n\nどこが原因かは伝えません。\n塞いだあと、同じ種類の穴が他に残っていないことまで示してください。",
    evidence: [
      "見えてしまう手順を、こちらが手元で再現できる形で書けている",
      "塞いだうえで、正規の利用者が締め出されていない",
      "同じ種類の穴を機械で探す手立てが残っている",
    ],
    requiredSkills: [
      { label: "認可" },
      { label: "マルチテナント設計" },
      { label: "SQL" },
      { label: "ログと監査" },
    ],
    aiAllowed: true,
    internetAllowed: true,
    status: "designing",
  },
  {
    slug: "migrate-db-without-downtime",
    code: "HELL #004",
    title: "本番を止めずにDBを移行せよ。",
    field: "インフラ / データベース",
    categories: ["engineering", "data"],
    brief:
      "稼働中のサービスのデータベースを、別の製品へ移します。\n止められる時間はありません。\n\n移行の途中で失敗したときに、どこまで戻せるかも設計に含めてください。\n「たぶん大丈夫」は答えになりません。",
    evidence: [
      "切替の各段で、失敗したときの戻り先が決まっている",
      "新旧の突き合わせで差分ゼロを示せている",
      "切替中も読み書きが通り続けたことを計測で示せている",
    ],
    requiredSkills: [
      { label: "レプリケーション" },
      { label: "スキーマ移行" },
      { label: "ロールバック設計" },
      { label: "監視" },
    ],
    aiAllowed: true,
    internetAllowed: true,
    status: "designing",
  },
  {
    slug: "grow-sales-in-30-days",
    code: "HELL #005",
    title: "この商品の売上を30日で伸ばせ。",
    field: "グロース / デザイン",
    categories: ["growth", "product"],
    brief:
      "アクセスはあります。買われません。\n広告費は増やせません。\n\n30日で何をどう変えるかを決めて、変えた結果を数字で示してください。\n伸びなかったときに、何が違ったのかを言えることまでが範囲です。",
    evidence: [
      "打ち手を選んだ理由が、事前のデータで説明されている",
      "効果を測る指標と期間を、動かす前に決めている",
      "伸びなかった場合の読み解きが書かれている",
    ],
    requiredSkills: [
      { label: "計測設計" },
      { label: "導線設計" },
      { label: "コピー" },
      { label: "A/Bテスト" },
    ],
    aiAllowed: true,
    internetAllowed: true,
    status: "designing",
  },
  {
    slug: "halve-signup-dropoff",
    code: "HELL #006",
    title: "登録離脱率を半分にせよ。",
    field: "プロダクト / UX / データ",
    categories: ["product", "growth", "data"],
    brief:
      "登録を始めた人の6割が、途中でやめます。\nどこでやめているかは、いまのログからは分かりません。\n\nまず分かるようにして、それから減らしてください。\n入力項目を削るだけで済むかどうかも、あなたが確かめます。",
    evidence: [
      "どの段でやめているかを、あとから誰でも同じ手順で出せる",
      "変更の前後を同じ定義で比べている",
      "削った項目が業務側で本当に不要だと確認されている",
    ],
    requiredSkills: [
      { label: "フォーム", roadmapSlug: "frontend", nodeId: "html-forms" },
      {
        label: "入力チェック",
        roadmapSlug: "frontend",
        nodeId: "html-validation",
      },
      {
        label: "アクセシビリティ",
        roadmapSlug: "frontend",
        nodeId: "quality-accessibility",
      },
      { label: "計測設計" },
      { label: "データ分析" },
    ],
    aiAllowed: true,
    internetAllowed: true,
    status: "designing",
  },
  {
    slug: "find-missing-revenue",
    code: "HELL #007",
    title: "売上が1,842万円合わない。",
    field: "SQL / データ",
    categories: ["data"],
    brief:
      "CFOから連絡が来ました。\n決済会社の8月売上と、社内ダッシュボードの数字が18,420,000円ずれています。\n\nどちらが正しいのかも分かりません。\n\n月曜の経営会議までに、原因を特定してください。",
    evidence: [
      "ずれの原因を、どのデータのどの行で起きているかまで説明できている",
      "8月の日別売上を出すクエリが、こちらが用意した別のデータでも同じ答えを出す",
      "どう数えたかを、あとから他の人が同じ手順で再現できる",
    ],
    requiredSkills: [
      { label: "SQL" },
      { label: "集計の設計" },
      { label: "データの突き合わせ" },
      { label: "数字の検算" },
    ],
    aiAllowed: true,
    internetAllowed: true,
    status: "open",
    format: "scenario",
    version: 1,
    runtime: { kind: "sql", manifestId: "sql-missing-revenue" },
    estimatedMinutes: { min: 90, max: 240 },
    tags: ["SQL", "データ品質", "集計"],
  },
];

export const CHALLENGE_BY_SLUG = new Map(CHALLENGES.map((c) => [c.slug, c]));

/** 一覧の絞り込み。all のときは並びを変えずに全部返す */
export function challengesForCategory(
  category: ChallengeCategory | "all",
): Challenge[] {
  if (category === "all") return CHALLENGES;
  return CHALLENGES.filter((c) => c.categories.includes(category));
}

/** 絞り込みタブに出す順。件数ゼロの分野は出さない */
export function challengeCategoriesInUse(): ChallengeCategory[] {
  const order: ChallengeCategory[] = [
    "engineering",
    "ai",
    "security",
    "data",
    "growth",
    "product",
  ];
  return order.filter((category) =>
    CHALLENGES.some((c) => c.categories.includes(category)),
  );
}
